import { toast } from "react-toastify"
import { uniq } from "~/utils/tsUtils"
import { api } from "~/utils/api"
import { invalidateAdjustmentQueries } from "~/utils/cacheUtils/adjustmentCacheHelpers"
import { useRefreshForecastDomain } from "~/utils/cacheUtils/forecastCacheHelpers"
import { showErrorToast } from "~/utils/toaster"
import { trackEvent } from "~/hooks/useTrackingService"
import { useMeasureInteraction } from "~/hooks/useMeasureInteraction"

/**
 * Query hook for fetching top adjustments for given variants
 * Moved from src/models/adjustment.tsx to centralize adjustment logic
 */
export const useAdjustments = (
  variantIds?: string[],
  warehouseIds?: string[],
  channel?: string,
  limit: number = 1000,
  enabled: boolean = true,
) =>
  api.adjustment.getTopAdjustments.useQuery(
    { variantIds, warehouseIds, channel, limit },
    { enabled },
  )

/**
 * Mutation hook for adding a new adjustment
 * Provides immediate cache updates for instant UI feedback
 */
export function useAddAdjustment() {
  const utils = api.useUtils()
  const refreshForecastDomain = useRefreshForecastDomain()
  const measureInteraction = useMeasureInteraction()

  return api.adjustment.add.useMutation({
    onMutate: () => measureInteraction("adjustment_add"),
    onSuccess: async (_data, variables) => {
      await refreshForecastDomain(variables.variantIds)
      toast.success("Adjustment was successfully created")
    },
    onError: (error) => {
      trackEvent("Adjustment Add Failed")
      showErrorToast(error)
      invalidateAdjustmentQueries(utils)
    },
  })
}

/**
 * Mutation hook for updating an existing adjustment
 * Provides immediate cache updates for instant UI feedback
 */
export function useUpdateAdjustment() {
  const utils = api.useUtils()
  const refreshForecastDomain = useRefreshForecastDomain()
  const measureInteraction = useMeasureInteraction()

  return api.adjustment.update.useMutation({
    onMutate: () => measureInteraction("adjustment_update"),
    onSuccess: async (_data, variables) => {
      await refreshForecastDomain(variables.variantIds)
      toast.success("Adjustment was successfully updated")
    },
    onError: (error) => {
      trackEvent("Adjustment Update Failed")
      showErrorToast(error)
      invalidateAdjustmentQueries(utils)
    },
  })
}

/**
 * Mutation hook for deleting an adjustment
 * Provides immediate cache updates for instant UI feedback
 */
export function useDeleteAdjustment() {
  const utils = api.useUtils()
  const refreshForecastDomain = useRefreshForecastDomain()
  const measureInteraction = useMeasureInteraction()

  return api.adjustment.delete.useMutation({
    onMutate: () => measureInteraction("adjustment_delete"),
    onSuccess: async (_data, variables) => {
      await refreshForecastDomain(variables.variantIds)
      toast.success("Adjustment was successfully deleted")
    },
    onError: (error) => {
      showErrorToast(error)
      invalidateAdjustmentQueries(utils)
    },
  })
}

/**
 * Mutation hook for deleting multiple adjustments at once
 * Provides immediate cache updates for instant UI feedback
 */
export function useDeleteManyAdjustments() {
  const utils = api.useUtils()
  const refreshForecastDomain = useRefreshForecastDomain()

  return api.adjustment.deleteMany.useMutation({
    onSuccess: async (_data, variables) => {
      const allVariantIds = uniq(variables.adjustments.flatMap((a) => a.variantIds))
      await refreshForecastDomain(allVariantIds)
      toast.success("Adjustments were successfully deleted")
    },
    onError: (error) => {
      showErrorToast(error)
      invalidateAdjustmentQueries(utils)
    },
  })
}
